// Die Peilung: wo liegt die naechste Solarwind-Front?
//
// Draussen sieht das Feld in jeder Richtung gleich aus. Wer mit halber Zelle
// unterwegs ist, kann heute nicht entscheiden, ob Weiterfliegen sich lohnt —
// die Front findet man nur zufaellig. Ein freigeschalteter Sensor macht daraus
// eine Entscheidung.
//
// **Das kostet keine einzige Nachricht.** Anomalien sind aus ihrer
// Chunk-Koordinate berechenbar (`sim/anomalies.ts`), also rechnet der Client
// die Umgebung selbst durch — auch weit jenseits des Laderadius. Es bleibt bei
// genau drei Client-Nachrichten (Gate FG-32), und solo funktioniert es wie
// online.
//
// Reine Funktion: aus Ort, Saat und Suchweite faellt der Fund. Kein
// Feldzustand, kein Phaser, in Node testbar (Gate FG-18).

import { balancing } from '../data/balancing';
import { anomaliesInChunk } from './anomalies';

/** Die ID der Art, auf die gepeilt wird — sie steht in der Tabelle. */
export const BEARING_KIND = 'solar_front';

export interface BearingHit {
  x: number;
  y: number;
  /** Luftlinie vom Schiff. */
  dist: number;
}

/**
 * Was der Scan gekostet hat — **messbar statt behauptet**.
 *
 * Die Zusage „die Kosten wachsen nicht mit der Suchweite" ist eine Zahl, keine
 * Meinung. Ohne einen Weg, sie zu lesen, koennte ein Test nur die Laufzeit
 * stoppen, und das ist auf einem geteilten Rechner kein Gate, sondern ein
 * Wuerfel. Der Aufrufer im Spiel uebergibt nichts und zahlt dafuer nichts.
 */
export interface BearingCost {
  /** Wie viele Kacheln der Scan angefasst hat. */
  chunks: number;
}

/**
 * Die **naechstgelegene** Front innerhalb der Suchweite — oder `null`.
 *
 * Zwei Anforderungen stehen hier gegeneinander, und beide sind echt:
 *
 * * **Es muss die naechste sein.** Wer ringweise nach aussen sucht und beim
 *   ersten Treffer abbricht, meldet womoeglich eine Front, die weiter weg ist
 *   als eine im selben oder im naechsten Ring. Der Pfeil zeigte dann sichtbar
 *   in die falsche Richtung, ohne dass irgendwo ein Fehler auftritt.
 * * **Es muss ein Tastendruck bleiben.** Die Vorgaengerfassung rechnete das
 *   ganze Quadrat um die Suchweite durch — bei Vollausbau sind das ueber
 *   30 000 Kacheln, und mit einer weiteren Welt waeren es Hunderttausende. Aus
 *   einer Antwort wuerde eine Pause, und die sieht nach einem Fehler aus.
 *
 * Beides zusammen geht, weil die Ringe eine **untere Schranke** liefern: eine
 * Kachel im Ring `m` liegt mindestens `(m - 1) * CHUNK` weit weg. Die Eins
 * darin ist kein Sicherheitsabstand, sondern der Grenzfall — steht das Schiff
 * genau auf einer Kachelecke, beruehrt eine Kachel des Rings 1 seinen Ort, und
 * ihr Abstand ist null. Sind also die Ringe bis `k - 1` abgearbeitet und steht
 * ein Fund dichter als `(k - 1) * CHUNK`, kann kein weiterer Ring ihn
 * unterbieten, und der Scan hoert auf — ohne das Ergebnis zu aendern.
 *
 * Damit haengen die Kosten an der Entfernung zur **Antwort**, nicht an der
 * Suchweite: Fronten liegen in etwa jeder dreissigsten Kachel, also endet der
 * Scan typisch nach ein paar Ringen. Nur wenn wirklich keine in Reichweite ist,
 * wird die ganze Fläche durchgerechnet — dann ist die Reichweite aber die des
 * ersten Ausbaus und die Fläche klein.
 *
 * Er laeuft auf Tastendruck, nicht in der Spielschleife (Gate FG-14).
 */
export function nearestFront(
  shipX: number, shipY: number, seed: number, range: number,
  era: number = balancing().eraFirst,
  cost?: BearingCost,
): BearingHit | null {
  if (cost) cost.chunks = 0;
  if (range <= 0) return null;
  const chunk = balancing().world.chunk;
  const scx = Math.floor(shipX / chunk);
  const scy = Math.floor(shipY / chunk);
  // Dieselbe aeussere Schranke wie die Vorgaengerfassung: weiter als
  // `floor(range / CHUNK) + 1` Ringe kann eine Front innerhalb der Suchweite
  // nicht liegen.
  const maxRing = Math.floor(range / chunk) + 1;

  let beste: BearingHit | null = null;
  // Die Weite steht als **Zahl** daneben, nicht nur im Fund. Die Schranke
  // vergleicht damit einen `number` statt ein Feld eines Objekts, das eine
  // verschachtelte Funktion beschreibt — sonst haelt die Typpruefung `beste` im
  // Schleifenkopf fuer `null` und den Zugriff darauf fuer unmoeglich.
  let besteDist = Infinity;
  const pruefe = (cx: number, cy: number): void => {
    if (cost) cost.chunks++;
    for (const blase of anomaliesInChunk(cx, cy, seed, era)) {
      if (blase.kindId !== BEARING_KIND) continue;
      const dist = Math.hypot(blase.x - shipX, blase.y - shipY);
      if (dist > range) continue;
      if (dist < besteDist) {
        besteDist = dist;
        beste = { x: blase.x, y: blase.y, dist };
      }
    }
  };

  for (let k = 0; k <= maxRing; k++) {
    // Die Schranke: nichts ab Ring k liegt naeher als (k - 1) * CHUNK.
    if (besteDist <= (k - 1) * chunk) break;
    for (let dy = -k; dy <= k; dy++) {
      if (dy === -k || dy === k) {
        // Die beiden vollen Zeilen des Rings — bei k = 0 genau die Mitte.
        for (let dx = -k; dx <= k; dx++) pruefe(scx + dx, scy + dy);
      } else {
        // Dazwischen nur die Flanken. Ueber `continue` zu gehen waere je Ring
        // wieder ein ganzes Quadrat und damit insgesamt eine Ordnung teurer.
        pruefe(scx - k, scy + dy);
        pruefe(scx + k, scy + dy);
      }
    }
  }
  return beste;
}
